/* eslint no-param-reassign: ["error", { "props": false }] */
import { createSlice, PayloadAction } from "@reduxjs/toolkit";
import { AppThunk } from "app/store";
import { UserProfile, getProfile } from "api/user";
import { getPersistedData, PersistedData } from "utils/persist";
import { getClubSuccess } from "features/calendar/reducer";

// user state
interface UserState {
    error: string | null,
    isLoading: boolean,
    profile: UserProfile | null,
    isCompanyProfile: boolean,
}

// initial state
const initialState: UserState = {
    error: null,
    isLoading: false,
    profile: null,
    isCompanyProfile: false,
};

// generic reducers
const startLoading = (state: UserState) => {
    state.isLoading = true;
    state.error = null;
};
const loadingFailed = (state: UserState, { payload }: PayloadAction<string>) => {
    state.isLoading = false;
    state.error = payload;
};

// slice
const userSlice = createSlice({
    name: "user",
    initialState,
    reducers: {
        clearProfile: (state) => { state.profile = null; },
        getProfileStart: startLoading,
        getProfileSuccess: (state, { payload }: PayloadAction<UserProfile>) => {
            state.isLoading = false;
            state.profile = payload;
            state.isCompanyProfile = (payload.userTypes || []).includes("company-profile");
        },
        getProfileFailure: loadingFailed,
    },
});

// inferred actions
const { getProfileStart, getProfileFailure } = userSlice.actions;
export const { clearProfile, getProfileSuccess } = userSlice.actions;

// fetch user profile
export const fetchUserProfile = (): AppThunk => async (dispatch) => {
    try {
        dispatch(getProfileStart());
        const userProfile = await getProfile();
        // switch to current club (if set)
        const currentClub = getPersistedData(PersistedData.CurrentClub);
        if (currentClub) {
            const clubData = userProfile.memberships.map((m) => m.club).find((c) => c.id === currentClub);
            if (clubData) dispatch(getClubSuccess(clubData));
        } else if (userProfile.memberships.length > 0) {
            const clubData = userProfile.memberships[0].club;
            dispatch(getClubSuccess(clubData));
        }
        dispatch(getProfileSuccess(userProfile));
    } catch (error: any) {
        dispatch(getProfileFailure(error.type));
    }
};

export default userSlice.reducer;
